import {
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  List,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconSearch } from "@tabler/icons-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export interface TutorialAction {
  title: string;
  location: string;
  steps: string[];
  note?: string;
}

export interface TutorialSection {
  title: string;
  description: string;
  icon: ReactNode;
  actions: TutorialAction[];
  hideActionArrows?: boolean;
}

interface RelatedLink {
  label: string;
  to: string;
  description: string;
}

interface TutorialPageShellProps {
  badge?: string;
  title: string;
  description: string;
  relatedLinks?: RelatedLink[];
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  resultCount?: number;
  children: ReactNode;
}

interface TutorialSectionCardProps {
  section: TutorialSection;
}

export function TutorialPageShell({
  badge,
  title,
  description,
  relatedLinks,
  searchValue,
  onSearchChange,
  resultCount,
  children,
}: TutorialPageShellProps) {
  const { t } = useTranslation("tutorial");

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Paper withBorder radius="lg" p="xl" shadow="sm">
          <Stack gap="md">
            <div>
              <Badge variant="light" mb="sm">
                {badge ?? t("common.badge")}
              </Badge>
              <Title order={1}>{title}</Title>
              <Text mt="sm" c="dimmed" maw={760}>
                {description}
              </Text>
            </div>
            {onSearchChange ? (
              <div>
                <TextInput
                  value={searchValue}
                  onChange={(event) => onSearchChange(event.currentTarget.value)}
                  placeholder={t("common.searchPlaceholder")}
                  leftSection={<IconSearch size={16} />}
                />
                <Text size="sm" c="dimmed" mt="xs">
                  {t("common.actionsShown", { count: resultCount ?? 0 })}
                </Text>
              </div>
            ) : null}
            {relatedLinks?.length ? (
              <Group gap="md" align="stretch">
                {relatedLinks.map((link) => (
                  <Card
                    key={link.to}
                    withBorder
                    radius="md"
                    p="md"
                    style={{ flex: 1, minWidth: 240 }}
                  >
                    <Stack gap="xs" h="100%" justify="space-between">
                      <div>
                        <Text fw={600}>{link.label}</Text>
                        <Text size="sm" c="dimmed" mt={4}>
                          {link.description}
                        </Text>
                      </div>
                      <Anchor component={Link} to={link.to} fw={600}>
                        {t("common.openPage")}
                      </Anchor>
                    </Stack>
                  </Card>
                ))}
              </Group>
            ) : null}
          </Stack>
        </Paper>
        {children}
      </Stack>
    </Container>
  );
}

export function filterTutorialSections(
  sections: TutorialSection[],
  query: string,
): TutorialSection[] {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return sections;
  }

  return sections
    .map((section) => {
      const matchingActions = section.actions.filter((action) => {
        const searchableText = [
          section.title,
          section.description,
          action.title,
          action.location,
          action.note,
          ...action.steps,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(normalizedQuery);
      });

      if (
        matchingActions.length > 0 ||
        section.title.toLowerCase().includes(normalizedQuery) ||
        section.description.toLowerCase().includes(normalizedQuery)
      ) {
        return {
          ...section,
          actions: matchingActions.length > 0 ? matchingActions : section.actions,
        };
      }

      return null;
    })
    .filter(Boolean) as TutorialSection[];
}

export function TutorialSectionCard({ section }: TutorialSectionCardProps) {
  return (
    <Card withBorder radius="lg" shadow="sm" p="xl">
      <Stack gap="lg">
        <Group gap="sm" align="center">
          <ThemeIcon size="lg" radius="xl" variant="light">
            {section.icon}
          </ThemeIcon>
          <div>
            <Title order={2}>{section.title}</Title>
            <Text c="dimmed">{section.description}</Text>
          </div>
        </Group>

        {section.actions.map((action, index) => (
          <Paper key={action.title} withBorder radius="md" p="lg">
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start" gap="sm">
                <div>
                  <Title order={4}>
                    {index + 1}. {action.title}
                  </Title>
                  <Text size="sm" c="dimmed">
                    {action.location}
                  </Text>
                </div>
                {section.hideActionArrows ? null : (
                  <ThemeIcon variant="light" radius="xl" color="blue">
                    <IconArrowRight size={16} />
                  </ThemeIcon>
                )}
              </Group>
              <List
                spacing="xs"
                icon={
                  <ThemeIcon size={20} radius="xl" color="teal" variant="light">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                {action.steps.map((step) => (
                  <List.Item key={step}>
                    <Text size="sm">{step}</Text>
                  </List.Item>
                ))}
              </List>
              {action.note ? (
                <Text size="sm" c="dimmed">
                  {action.note}
                </Text>
              ) : null}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Card>
  );
}
